import { useEffect } from 'react';
import type { RefObject } from 'react';

export type AnchorPlacement =
	| 'bottomLeft'
	| 'bottomRight'
	| 'topLeft'
	| 'topRight'
	| 'overlay';

export interface AnchorOptions {
	placement: AnchorPlacement;
	inheritWidth?: boolean;
}

const AnchorProperties: string[] = [
	'position',
	'inset',
	'top',
	'right',
	'bottom',
	'left',
	'min-inline-size',
];

/**
 *	WebKit reports anchor positioning as supported and then offsets, sizes and
 *	hides the popover wrongly, so it is placed by script like a browser without
 *	it. Every browser on iOS is WebKit, which is why `CriOS` and `FxiOS` count.
 *
 *	A scripted pair carries `data-anchor="script"`, which every entity's anchor
 *	CSS excludes, so none of it reaches a browser that gets it wrong.
 */
function scripted(): boolean {
	if (typeof CSS === 'undefined' || typeof navigator === 'undefined') {
		return false;
	}

	const agent: string = navigator.userAgent;
	const webkit: boolean =
		/AppleWebKit/.test(agent) && !/(Chrome|Chromium|Edg)\//.test(agent);

	return webkit || !CSS.supports('anchor-name: --anchor');
}

/**
 *	Inside a fixed container, such as a modal or a popout panel, the element
 *	stays put while the page scrolls, so the popover has to as well.
 */
function inFixedContainer(anchor: HTMLElement): boolean {
	for (
		let element: HTMLElement | null = anchor.parentElement;
		element;
		element = element.parentElement
	) {
		if (getComputedStyle(element).position === 'fixed') {
			return true;
		}
	}

	return false;
}

function place(
	anchor: HTMLElement,
	popover: HTMLElement,
	{ placement, inheritWidth = false }: AnchorOptions
): void {
	const box: DOMRect = anchor.getBoundingClientRect();
	const width: number = document.documentElement.clientWidth;
	const height: number = document.documentElement.clientHeight;
	const style: CSSStyleDeclaration = popover.style;
	const fixed: boolean = inFixedContainer(anchor);

	/**
	 *	Page coordinates, not viewport ones: absolutely positioned, the popover
	 *	scrolls with the page on its own, where a fixed one moved by a scroll
	 *	listener trails the page it is pinned to. An element in a fixed
	 *	container does not scroll with the page, so its popover is fixed too.
	 */
	const scrollX: number = fixed ? 0 : window.scrollX;
	const scrollY: number = fixed ? 0 : window.scrollY;

	const rect = {
		top: box.top + scrollY,
		right: box.right + scrollX,
		bottom: box.bottom + scrollY,
		left: box.left + scrollX,
		width: box.width,
	};

	style.setProperty('position', fixed ? 'fixed' : 'absolute');
	style.setProperty('inset', 'auto');

	switch (placement) {
		case 'bottomLeft':
			style.setProperty('top', `${rect.bottom}px`);
			style.setProperty('left', `${rect.left}px`);
			break;
		case 'bottomRight':
			style.setProperty('top', `${rect.bottom}px`);
			style.setProperty('right', `${width - rect.right}px`);
			break;
		case 'topLeft':
			style.setProperty('bottom', `${height - rect.top}px`);
			style.setProperty('left', `${rect.left}px`);
			break;
		case 'topRight':
			style.setProperty('bottom', `${height - rect.top}px`);
			style.setProperty('right', `${width - rect.right}px`);
			break;
		case 'overlay':
			style.setProperty('top', `${rect.top}px`);
			style.setProperty('left', `${rect.left}px`);
			break;
	}

	if (inheritWidth) {
		style.setProperty('min-inline-size', `${rect.width}px`);
	}
}

function clear(popover: HTMLElement): void {
	for (const property of AnchorProperties) {
		popover.style.removeProperty(property);
	}
}

export function anchorPopover(
	anchor: HTMLElement,
	popover: HTMLElement,
	options: AnchorOptions
): () => void {
	if (!scripted()) {
		return () => {};
	}

	const follow = (): void => place(anchor, popover, options);

	function handleScroll(event: Event): void {
		if (event.target !== document) {
			follow();
		}
	}

	anchor.dataset.anchor = 'script';
	popover.dataset.anchor = 'script';

	function start(): void {
		follow();
		window.addEventListener('scroll', handleScroll, {
			capture: true,
			passive: true,
		});
		window.addEventListener('resize', follow, { passive: true });
	}

	function stop(): void {
		window.removeEventListener('scroll', handleScroll, { capture: true });
		window.removeEventListener('resize', follow);
	}

	function handleBeforeToggle(event: Event): void {
		if ((event as ToggleEvent).newState === 'open') {
			start();
		} else {
			stop();
		}
	}

	popover.addEventListener('beforetoggle', handleBeforeToggle);

	// Before 17, WebKit has no Popover API and `matches` throws on the `:popover-open` it doesn't know.
	if ('showPopover' in popover && popover.matches(':popover-open')) {
		start();
	}

	return () => {
		popover.removeEventListener('beforetoggle', handleBeforeToggle);
		stop();
		clear(popover);
		delete anchor.dataset.anchor;
		delete popover.dataset.anchor;
	};
}

export function useAnchorPosition(
	anchor: RefObject<HTMLElement | null>,
	popover: RefObject<HTMLElement | null>,
	enabled: boolean,
	{ placement, inheritWidth }: AnchorOptions
): void {
	useEffect(() => {
		if (!enabled || !anchor.current || !popover.current) {
			return;
		}

		return anchorPopover(anchor.current, popover.current, {
			placement,
			inheritWidth,
		});
	}, [anchor, popover, enabled, placement, inheritWidth]);
}
